import { CompactUserResponse, UserId, UserResponse } from '@business/entities/account';
import { OrgId } from '@business/entities/organization';
import {
  CompactSupportTicketResponse,
  SupportTicketActionName,
  SupportTicketPriority,
  SupportTicketStatus,
  TicketAdditionalResponse,
  TicketId,
  TicketSearchParams,
} from '@business/entities/SupportTicket';
import { CommonService, HTMLService } from '@business/services';
import { TicketService } from '@business/services/ticket_service';
import { Button } from '@core/components/Button';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { formControl, formGroup, useFormConfig } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { PaginatedController } from '@core/utils/paginated';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { openSelectOrgModal } from '@modules/common';
import { openTicketOrg } from '@modules/support/TicketDetailPage/Components/TicketOrgModal';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { IDataTableColumn } from 'react-data-table-component-footer';
import { Link } from 'react-router-dom';
import { Badge, DropdownMenu, DropdownToggle, UncontrolledDropdown, UncontrolledTooltip } from 'reactstrap';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TableColumn } from 'react-data-table-component';

interface SubjectItemProps {
  ticket: CompactSupportTicketResponse;
}

const SubjectItem = memo((props: SubjectItemProps) => {
  const { ticket } = props;

  return (
    <div className="py-2 d-flex" style={{ flex: 1, alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <Link to={`/support-tickets/detail/${ticket.id}`} className="text-primary">
          <h6 className="mb-1">{ticket.subject}</h6>
        </Link>
        <div className="font-size-12 pt-1">{ticket.fromEmail}</div>
      </div>
      {ticket.tasks > 0 && (
        <span className="text-muted mr-3 font-size-12" style={{ opacity: 0.8 }}>
          <i className="fa fa-sitemap mr-2"></i>
          <span style={{ fontWeight: '600' }}>{`${ticket.completedTasks}/${ticket.tasks}`}</span>
        </span>
      )}

      <div className="d-flex flex-column align-items-end">
        <div id={`ticket-tooltip-${ticket.id}`} className="hover-pointer" style={{ padding: 5 }}>
          <i className="fa fa-eye"></i>
        </div>

        <UncontrolledTooltip placement="top" target={`ticket-tooltip-${ticket.id}`}>
          <div style={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            {HTMLService.toReact(ticket.lastThreadText)}
          </div>
        </UncontrolledTooltip>
        <div>
          {!R.isEmpty(ticket.categoryLabel) && (
            <Badge color={'info'} className={`badge-soft font-size-10 px-2 py-1 mr-2`}>
              {ticket.categoryLabel}
            </Badge>
          )}
          {ticket.importId === '0' && !R.isEmpty(ticket.tenantName) && (
            <Badge color={'primary'} className={`badge-soft font-size-10 px-2 py-1`}>
              Escalated to {ticket.tenantName}
            </Badge>
          )}
        </div>
      </div>
    </div>

    // <div className="py-2 d-flex" style={{ flex: 1, alignItems: 'center' }}>
    //   <div style={{ float: 'left', width: '100%', position: 'relative' }}>
    //     <h6 className="mb-1">
    //       <Link to={`/support-tickets/detail/${ticket.id}`} className="text-primary">
    //         {ticket.subject}
    //       </Link>
    //     </h6>
    //     <div className="hover-pointer" style={{ padding: 5, position: 'relative', marginTop: '-14px' }}>
    //       <div className="hover-pointer" id={`ticket-tooltip-${ticket.id}`} style={{ float: 'right' }}>
    //         <i className="fa fa-eye"></i>
    //       </div>
    //     </div>
    //     <div className="font-size-12 pt-1" style={{ marginTop: 2 }}>
    //       {ticket.fromEmail}
    //     </div>
    //   </div>
    //   <UncontrolledTooltip placement="top" target={`ticket-tooltip-${ticket.id}`}>
    //     <div style={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
    //       {HTMLService.toReact(ticket.lastThreadText)}
    //     </div>
    //   </UncontrolledTooltip>
    // </div>
  );
});

export interface TicketListingTableProps {
  paginated: PaginatedController<CompactSupportTicketResponse, string, TicketSearchParams, TicketAdditionalResponse>;
  users: CompactUserResponse[] | UserResponse[];
  tickets: CompactSupportTicketResponse[];
  onAssignTo(id: TicketId, action: SupportTicketActionName, actionId: string): Observable<any>;
  onUpdateBulk(ids: TicketId[], status: SupportTicketStatus): Observable<any>;
  updateTicketAction(ticketId: TicketId, action: SupportTicketActionName, actionId: string): Observable<any>;
  initialSearchString?: string;
}

export const TicketListingTable = memo((props: TicketListingTableProps) => {
  const { tickets, users, paginated, onAssignTo, onUpdateBulk, updateTicketAction, initialSearchString } = props;
  const [selectedIds, setSelectedIds] = useState<TicketId[]>([]);
  const [toggledClearRows, setToggleClearSelectedRows] = useState(false);

  const form = useMemo(() => formGroup({ action: formControl<SupportTicketStatus>({}) }), []);

  const onSelectedRowsChange = useCallback(({ selectedRows }) => {
    const ids = (selectedRows as CompactSupportTicketResponse[]).map(t => t.id);
    setSelectedIds(ids);
    setToggleClearSelectedRows(false);
  }, []);

  const onPriorityUpdate = useCallback(
    (ticketId: TicketId, priority: SupportTicketPriority) => {
      updateTicketAction(ticketId, SupportTicketActionName.priority, priority).subscribe();
    },
    [updateTicketAction],
  );

  const controls = useMemo(() => {
    return tickets.map(ticket =>
      formControl<UserId>({
        placeholder: 'Assignee',
        initialValue: ticket.assignedTo?.id,
      }),
    );
  }, [tickets]);

  const updateTicketAssignee = useCallback(
    (ticketId: TicketId, userId: UserId) => {
      onAssignTo(ticketId, SupportTicketActionName.assignedTo, userId).subscribe();
    },
    [onAssignTo],
  );

  const usersItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id'), [users]);

  const onSort = useCallback(
    (column: TableColumn<CompactSupportTicketResponse>, sortDirection: 'asc' | 'desc') => {
      paginated.patchQuery({ sortBy: column.sortField as string, sortDirection }).subscribe();
    },
    [paginated],
  );

  const columns: GCColumn<CompactSupportTicketResponse>[] = useMemo(
    () => [
      {
        id: 'priority',
        width: '50px',
        name: ' ',
        sortField: 'priority',
        sortable: true,
        cell: ticket => (
          <UncontrolledDropdown style={{ width: '100%' }}>
            <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center">
              <i style={{ color: TicketService.getPriorityColor(ticket.priority) }} className="fa fa-flag"></i>
            </DropdownToggle>

            <DropdownMenu container="body" className="dd-menu_outside">
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.normal)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.normal) }} className="fa fa-flag mr-2"></i>Normal
              </a>
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.major)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.major) }} className="fa fa-flag mr-2"></i>Major
              </a>
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.critical)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.critical) }} className="fa fa-flag mr-2"></i>Critical
              </a>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
      {
        id: 'status',
        name: 'Status',
        width: '100px',
        sortable: true,
        sortField: 'status',
        cell: ticket => (
          <Badge pill color={TicketService.getStatusIntent(ticket.status)} className={`badge-soft font-size-12 px-2 py-1`}>
            {TicketService.getStatusTitle(ticket.status, true)}
          </Badge>
        ),
      },
      {
        id: 'subject',
        name: 'Subject',
        minWidth: '300px',
        sortable: true,
        sortField: 'subject',
        cell: ticket => <SubjectItem ticket={ticket} />,
      },
      {
        id: 'org',
        name: 'Organizations',
        maxWidth: '200px',
        sortable: true,
        sortField: 'organization',
        cell: ticket => (
          <div className="py-2">
            {!R.isEmpty(ticket.orgName) && (
              <a className="text-primary" href={`/organizations/${ticket.orgId}`} target="_blank" rel="noopener noreferrer">
                {ticket.orgName}
              </a>
            )}
            <a
              className="text-primary"
              onClick={() => {
                ticket.orgId != '0'
                  ? openSelectOrgModal({
                      onSelect: (orgId: OrgId) => updateTicketAction(ticket.id, SupportTicketActionName.orgId, orgId),
                    })
                  : openTicketOrg({ ticketId: ticket.id, email: ticket.fromEmail, name: ticket.fromName });
              }}
            >
              {R.isEmpty(ticket.orgName) && <span>---</span>}
              <i className="bx bx-edit ml-2"></i>
            </a>
          </div>
        ),
      },

      {
        id: 'assignedTo',
        name: 'Assigned To',
        maxWidth: '205px',
        sortable: true,
        sortField: 'assignedTo',
        cell: (ticket, index) => (
          <div className="w-100">
            {!!controls[index] && (
              <FormSelect withoutGroup items={usersItems} control={controls[index]} onChange={userId => updateTicketAssignee(ticket.id, userId)} />
            )}
          </div>
        ),
      },
      {
        id: 'lastActivity',
        name: 'Last Activity',
        maxWidth: '125px',
        sortable: true,
        sortField: 'lastActivity',
        cell: ticket => (
          <div className="py-2">
            <p className="mb-2 l-h-1 ">{ticket.lastActivityTime ? toMoment(ticket.lastActivityTime).fromNow() : ''}</p>
            <div className="l-h-1 text-muted font-size-12">{Formatter.formatTime(ticket.lastActivityTime, 'MMM D')}</div>
          </div>
        ),
      },

      {
        id: 'createdAt',
        name: 'Created',
        maxWidth: '125px',
        sortable: true,
        sortField: 'createdAt',
        cell: ticket => (
          <div className="l-h-1">
            {Formatter.formatTime(ticket.createdAt, toMoment(ticket.createdAt).get('year') == toMoment().get('year') ? 'MMM D' : 'MMM D, YYYY')}
          </div>
        ),
      },
    ],
    [controls, onPriorityUpdate, updateTicketAction, updateTicketAssignee, usersItems],
  );

  const statusItems = useMemo(
    () => CommonService.selectItems(TicketService.allStatuses(true), i => TicketService.getStatusTitle(i, false, true)),
    [],
  );

  const onSubmit = useCallback(
    ({ action }) => {
      return onUpdateBulk(selectedIds, action).pipe(tap(() => setToggleClearSelectedRows(true)));
    },
    [onUpdateBulk, selectedIds],
  );

  useFormConfig(form, { onSubmit });

  return (
    <div>
      <GCTable
        initialSearchString={initialSearchString}
        columns={columns}
        data={tickets}
        paginated={paginated}
        selectableRows
        selectableRowsHighlight
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={toggledClearRows}
        sortServer
        onSort={onSort}
        defaultSortField="lastActivityTime"
        defaultSortAsc={false}
        paginationPerPage={30}
        paginationRowsPerPageOptions={[30, 60, 100]}
      >
        {selectedIds.length > 0 && (
          <div style={{ flex: 1, flexDirection: 'row', display: 'flex' }}>
            <div style={{ width: 200, marginRight: 10 }}>
              <FormSelect withoutGroup control={form.controls.action} items={statusItems} />
            </div>
            <Button onClick={() => form.submit()}>GO</Button>
          </div>
        )}
      </GCTable>
    </div>
  );
});
